import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import type { Customer } from "@shared/types";
import { Customers } from "../lib/api";
import { describeError } from "../lib/errors";

interface Props {
  customer: Customer | null;
  customers: Customer[];
  onChange: (customer: Customer) => void;
}

export function CustomerInlinePicker({ customer, customers, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [list, setList] = useState<Customer[]>(customers);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => setList(customers), [customers]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((c) =>
      [c.name, c.ico, c.dic, c.ic_dph]
        .filter(Boolean)
        .some((s) => s!.toLowerCase().includes(q))
    );
  }, [list, query]);

  return (
    <div class="cust-picker" ref={ref}>
      <button
        type="button"
        class={`cust-picker__name ${customer ? "" : "cust-picker__name--empty"}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        {customer ? customer.name : "— vyberte odberateľa —"}
        <span class="cust-picker__chev" aria-hidden>▾</span>
      </button>

      {customer && (
        <div class="cust-picker__details">
          {(customer.address_line1 ||
            customer.address_zip ||
            customer.address_city ||
            customer.address_country) && (
            <p>
              {[
                customer.address_line1,
                `${customer.address_zip ?? ""} ${customer.address_city ?? ""}`.trim(),
                customer.address_country,
              ]
                .filter(Boolean)
                .join(", ")}
            </p>
          )}
          <p class="cust-picker__ids">
            {customer.ico && <>IČO: {customer.ico}</>}
            {customer.dic && <> · DIČ: {customer.dic}</>}
            {customer.ic_dph && <> · IČ DPH: {customer.ic_dph}</>}
          </p>
        </div>
      )}

      {open && (
        <div class="combobox__panel cust-picker__panel">
          <input
            type="text"
            class="combobox__search"
            placeholder="Hľadať odberateľa…"
            value={query}
            onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
            autoFocus
          />
          <ul class="combobox__list">
            {filtered.map((c) => (
              <li
                key={c.id}
                class="combobox__item"
                onClick={() => {
                  onChange(c);
                  setOpen(false);
                  setQuery("");
                }}
              >
                <span>{c.name}</span>
                {c.ico && <span class="muted">IČO {c.ico}</span>}
              </li>
            ))}
            {!filtered.length && (
              <li class="combobox__empty muted">Žiadne výsledky</li>
            )}
          </ul>
          <button
            type="button"
            class="combobox__add"
            onClick={() => {
              setOpen(false);
              setShowNew(true);
            }}
          >
            + Nový odberateľ
          </button>
        </div>
      )}

      {showNew && (
        <NewCustomerDialog
          onClose={() => setShowNew(false)}
          onCreated={(c) => {
            setList((prev) =>
              [...prev, c].sort((a, b) => a.name.localeCompare(b.name, "sk"))
            );
            onChange(c);
            setShowNew(false);
          }}
        />
      )}
    </div>
  );
}

function NewCustomerDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (c: Customer) => void;
}) {
  const [form, setForm] = useState({
    name: "",
    address_line1: "",
    address_city: "",
    address_zip: "",
    address_country: "Slovensko",
    ico: "",
    dic: "",
    ic_dph: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function update<K extends keyof typeof form>(key: K, v: string) {
    setForm((f) => ({ ...f, [key]: v }));
  }

  async function submit(e: Event) {
    e.preventDefault();
    if (!form.name.trim()) {
      setErr("Názov je povinný");
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const r = await Customers.create(form);
      onCreated(r.customer);
    } catch (e) {
      setErr(describeError(e));
      setSaving(false);
    }
  }

  return (
    <div class="modal" role="dialog" aria-modal="true">
      <div class="modal__backdrop" onClick={onClose} />
      <form class="modal__card" onSubmit={submit}>
        <h2>Nový odberateľ</h2>
        <label class="field">
          <span>Názov *</span>
          <input
            value={form.name}
            onInput={(e) => update("name", (e.target as HTMLInputElement).value)}
            required
            autoFocus
          />
        </label>
        <div class="grid-2">
          <label class="field">
            <span>Ulica a č.</span>
            <input
              value={form.address_line1}
              onInput={(e) =>
                update("address_line1", (e.target as HTMLInputElement).value)
              }
            />
          </label>
          <label class="field">
            <span>Mesto</span>
            <input
              value={form.address_city}
              onInput={(e) =>
                update("address_city", (e.target as HTMLInputElement).value)
              }
            />
          </label>
          <label class="field">
            <span>PSČ</span>
            <input
              value={form.address_zip}
              onInput={(e) =>
                update("address_zip", (e.target as HTMLInputElement).value)
              }
            />
          </label>
          <label class="field">
            <span>Štát</span>
            <input
              value={form.address_country}
              onInput={(e) =>
                update("address_country", (e.target as HTMLInputElement).value)
              }
            />
          </label>
          <label class="field">
            <span>IČO</span>
            <input
              value={form.ico}
              onInput={(e) => update("ico", (e.target as HTMLInputElement).value)}
            />
          </label>
          <label class="field">
            <span>DIČ</span>
            <input
              value={form.dic}
              onInput={(e) => update("dic", (e.target as HTMLInputElement).value)}
            />
          </label>
          <label class="field">
            <span>IČ DPH</span>
            <input
              value={form.ic_dph}
              onInput={(e) =>
                update("ic_dph", (e.target as HTMLInputElement).value)
              }
            />
          </label>
        </div>
        {err && <p class="error">{err}</p>}
        <div class="modal__actions">
          <button type="button" class="btn" onClick={onClose} disabled={saving}>
            Zrušiť
          </button>
          <button class="btn primary" type="submit" disabled={saving}>
            {saving ? "Ukladám…" : "Uložiť"}
          </button>
        </div>
      </form>
    </div>
  );
}
